/**
 * A custom element for a quantity input with increment and decrement buttons.
 * This element allows users to adjust a quantity value within specified min and max limits.
 *
 * Usage:
 * ```html
 * <quantity-input>
 *     <button data-quantity-decrement>-</button>
 *     <button data-quantity-increment>+</button>
 *     <input type="number" data-quantity-value min="0" max="10" step="1" />
 * </quantity-input>
 * ```
 */
class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this._max = Infinity;
        this._min = 0;
        this._step = 1;
        this.setup();
    }

    connectedCallback() {
        this.setup();
    }

    setup() {
        if (this._instantiated) return;

        this.input = this.querySelector("[data-quantity-value]");
        this.incrementButton = this.querySelector("[data-quantity-increment]");
        this.decrementButton = this.querySelector("[data-quantity-decrement]");

        if (!this.input) return;

        this._max = this.getMax();
        this._min = this.getMin();
        this._step = this.getStep();

        this?.incrementButton?.addEventListener(
            "click",
            this.increment.bind(this),
        );

        this?.decrementButton?.addEventListener(
            "click",
            this.decrement.bind(this),
        );

        this.render();
        this._instantiated = true;
    }

    getStep() {
        const stepAttr = this.input.getAttribute("step");
        return stepAttr ? parseFloat(stepAttr) : 1;
    }

    getMax() {
        const maxAttr = this.input.getAttribute("max");
        return maxAttr ? parseFloat(maxAttr) : Infinity;
    }

    getMin() {
        const minAttr = this.input.getAttribute("min");
        return minAttr ? parseFloat(minAttr) : 0;
    }

    getCurrentValue() {
        return parseFloat(this.input.value) || 0;
    }

    setValue(newValue) {
        const step = this.getStep();
        const precision = (step.toString().split(".")[1] || "").length;
        this.input.value = newValue.toFixed(precision);
        this.triggerInputEvents();

        this.render();
    }

    increment() {
        const currentValue = this.getCurrentValue();
        if (currentValue < this._max) {
            this.setValue(Math.min(currentValue + this._step, this._max));
        }
    }

    decrement() {
        const currentValue = this.getCurrentValue();
        if (currentValue > this._min) {
            this.setValue(Math.max(currentValue - this._step, this._min));
        }
    }

    triggerInputEvents() {
        this.input.dispatchEvent(new Event("change", { bubbles: true }));
        this.input.dispatchEvent(new Event("input", { bubbles: true }));
    }

    render() {
        const currentValue = this.getCurrentValue();

        if (this.incrementButton) {
            const atMax = currentValue >= this._max;
            this.incrementButton.disabled = atMax;
            this.incrementButton.setAttribute("aria-disabled", atMax);
        }

        if (this.decrementButton) {
            const atMin = currentValue <= this._min;
            this.decrementButton.disabled = atMin;
            this.decrementButton.setAttribute("aria-disabled", atMin);
        }
    }

    static get observedAttributes() {
        return ["min", "max", "step"];
    }

    attributeChangedCallback(name) {
        if (name === "max") this._max = this.getMax();
        if (name === "min") this._min = this.getMin();
        if (name === "step") this._step = this.getStep();

        this.render();
    }
}

customElements.define("quantity-input", QuantityInput);
